import React, { useCallback, useEffect, useState } from 'react';
import {
  listPaymentBatches, createPaymentBatch, getPaymentBatch, updatePaymentBatch, deletePaymentBatch,
  addPaymentSalaries, getReimbursementCandidates, addPaymentReimbursements, addPaymentOther,
  updatePaymentLine, deletePaymentLine, exportPaymentBatch, markPaymentBatchPaid,
  listPayees, createPayee, updatePayee, deletePayee,
  listPayrollRuns, getStaff, getBillingEntities,
} from '../api';
import { formatRupeesUI, formatDateDdMmmYy } from '../utils/format';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const today = () => new Date().toISOString().slice(0, 10);
const errMsg = (e: any, fallback: string) => e?.response?.data?.message || fallback;

interface Entity { id: number; name: string; bankAccount?: string | null }
interface Line {
  id: number; type: 'SALARY' | 'REIMBURSEMENT' | 'OTHER'; beneficiaryName: string; ifsc: string; account: string;
  entityId: number | null; entityName: string | null; entityOverride: boolean; amount: number; narration: string;
  mode: string | null; sourceAmount: number | null; claimNumber: string | null; issues: string[];
}
interface BatchView {
  batch: { id: number; paymentDate: string; title?: string | null; status: 'DRAFT' | 'PAID' };
  lines: Line[];
  entities: { entityId: number | null; entityName: string; count: number; total: number; issueCount: number }[];
}

type Tab = 'batches' | 'payees';

const Payments: React.FC = () => {
  const [tab, setTab] = useState<Tab>('batches');
  const [openId, setOpenId] = useState<number | null>(null);
  return (
    <div className="space-y-0">
      <div className="mb-4">
        <h2 className="text-2xl font-bold text-gray-900">Payments</h2>
        <p className="text-sm text-gray-500 mt-1">Salaries, approved reimbursements and other payments — exported as bank upload files per entity</p>
      </div>
      <div className="flex gap-1 border-b border-gray-200 mb-6">
        {([['batches', '💳 Payment Batches'], ['payees', '👥 Other Payees']] as const).map(([k, l]) => (
          <button key={k} onClick={() => { setTab(k); setOpenId(null); }}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === k ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>{l}</button>
        ))}
      </div>
      {tab === 'batches' && (openId
        ? <BatchDetail id={openId} onBack={() => setOpenId(null)} />
        : <BatchList onOpen={setOpenId} />)}
      {tab === 'payees' && <PayeesTab />}
    </div>
  );
};

// ── BATCH LIST ───────────────────────────────────────────────────────────────

const BatchList: React.FC<{ onOpen: (id: number) => void }> = ({ onOpen }) => {
  const [rows, setRows] = useState<any[]>([]);
  const [date, setDate] = useState(today());
  const [title, setTitle] = useState('');
  const [error, setError] = useState('');
  const load = () => listPaymentBatches().then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    setError('');
    try { const r = await createPaymentBatch({ paymentDate: date, title: title || undefined }); onOpen(r.data.id); }
    catch (e) { setError(errMsg(e, 'Could not create batch')); }
  };

  return (
    <div className="space-y-6">
      <div className="card p-4">
        <h3 className="font-semibold text-gray-800 mb-3">New payment batch</h3>
        <div className="flex flex-wrap items-end gap-3">
          <div><label className="label">Payment date</label><input type="date" className="input-field" value={date} onChange={(e) => setDate(e.target.value)} /></div>
          <div><label className="label">Title (optional)</label><input className="input-field w-64" value={title} placeholder="e.g. September salaries" onChange={(e) => setTitle(e.target.value)} /></div>
          <button className="btn-primary" onClick={create}>Create batch</button>
        </div>
        {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
      </div>
      <div className="card p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs uppercase"><tr>
            <th className="table-cell text-left">Date</th><th className="table-cell text-left">Title</th>
            <th className="table-cell text-right">Payments</th><th className="table-cell text-right">Total</th>
            <th className="table-cell text-left">Status</th><th className="table-cell" />
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="table-cell text-center text-gray-400 py-6">No batches yet</td></tr>}
            {rows.map((b) => (
              <tr key={b.id} className="border-t border-gray-100">
                <td className="table-cell">{formatDateDdMmmYy(b.paymentDate)}</td>
                <td className="table-cell">{b.title || '—'}</td>
                <td className="table-cell text-right">{b.lineCount}</td>
                <td className="table-cell text-right">₹{formatRupeesUI(b.total)}</td>
                <td className="table-cell"><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${b.status === 'PAID' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{b.status}</span></td>
                <td className="table-cell"><button className="text-blue-600 hover:underline" onClick={() => onOpen(b.id)}>Open</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── BATCH DETAIL ─────────────────────────────────────────────────────────────

const BatchDetail: React.FC<{ id: number; onBack: () => void }> = ({ id, onBack }) => {
  const [view, setView] = useState<BatchView | null>(null);
  const [msg, setMsg] = useState('');
  const [problems, setProblems] = useState<{ lineId: number; name: string; issues: string[] }[]>([]);
  const [entities, setEntities] = useState<Entity[]>([]);
  const [panel, setPanel] = useState<'' | 'salary' | 'reimb' | 'other'>('');

  const load = useCallback(() => getPaymentBatch(id).then((r) => setView(r.data)), [id]);
  useEffect(() => { load(); getBillingEntities().then((r) => setEntities(r.data)); }, [load]);

  if (!view) return <p className="text-gray-400 text-sm">Loading…</p>;
  const { batch, lines } = view;
  const draft = batch.status === 'DRAFT';
  const total = lines.reduce((s, l) => s + l.amount, 0);

  const saveLine = async (l: Line, patch: { amount?: number; narration?: string; entityId?: number | null }) => {
    setMsg('');
    try { await updatePaymentLine(l.id, patch); await load(); } catch (e) { setMsg(errMsg(e, 'Save failed')); await load(); }
  };

  const download = async (entityId: number, name: string) => {
    setMsg(''); setProblems([]);
    try {
      const res = await exportPaymentBatch(id, entityId);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.ms-excel' }));
      const a = document.createElement('a'); a.href = url;
      a.download = `Payments_${name.replace(/[^\w-]+/g, '_')}_${batch.paymentDate.slice(0, 10)}.xls`; a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      // The blob error body needs parsing to show the list of problems.
      try {
        const body = JSON.parse(await e.response.data.text());
        setMsg(body.message); setProblems(body.problems || []);
      } catch { setMsg('Download failed'); }
    }
  };

  const markPaid = async () => {
    if (!window.confirm('Mark this batch as paid? Reimbursement claims in it become PAID and the batch locks.')) return;
    try { const r = await markPaymentBatchPaid(id); setMsg(`Batch locked. ${r.data.claimsPaid} claim(s) marked paid.`); await load(); }
    catch (e) { setMsg(errMsg(e, 'Failed')); }
  };
  const remove = async () => {
    if (!window.confirm('Delete this draft batch? Its salaries and claims become available again.')) return;
    await deletePaymentBatch(id); onBack();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button className="text-blue-600 hover:underline text-sm" onClick={onBack}>← Back to batches</button>
        <div className="flex items-center gap-3">
          {draft && <input type="date" className="input-field" value={batch.paymentDate.slice(0, 10)}
            onChange={(e) => updatePaymentBatch(id, { paymentDate: e.target.value }).then(load)} />}
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${draft ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>{batch.status}</span>
          {draft && <button className="text-red-500 text-sm hover:underline" onClick={remove}>Delete batch</button>}
        </div>
      </div>

      <h3 className="text-lg font-semibold text-gray-800">
        {batch.title || 'Payment batch'} · {formatDateDdMmmYy(batch.paymentDate)} · {lines.length} payments · ₹{formatRupeesUI(total)}
      </h3>

      {draft && (
        <div className="card p-3 flex flex-wrap gap-2">
          <button className="btn-secondary text-sm" onClick={() => setPanel(panel === 'salary' ? '' : 'salary')}>+ Salaries from payroll</button>
          <button className="btn-secondary text-sm" onClick={() => setPanel(panel === 'reimb' ? '' : 'reimb')}>+ Approved reimbursements</button>
          <button className="btn-secondary text-sm" onClick={() => setPanel(panel === 'other' ? '' : 'other')}>+ Other payment</button>
        </div>
      )}
      {draft && panel === 'salary' && <SalaryPanel batchId={id} onDone={(m) => { setMsg(m); setPanel(''); load(); }} />}
      {draft && panel === 'reimb' && <ReimbPanel batchId={id} onDone={(m) => { setMsg(m); setPanel(''); load(); }} />}
      {draft && panel === 'other' && <OtherPanel batchId={id} entities={entities} onDone={() => { setPanel(''); load(); }} />}

      {msg && <p className="text-sm text-gray-700 bg-blue-50 border border-blue-100 rounded p-2">{msg}</p>}
      {problems.length > 0 && (
        <ul className="text-sm text-red-700 bg-red-50 border border-red-100 rounded p-2 list-disc pl-6">
          {problems.map((p) => <li key={p.lineId}><b>{p.name}</b>: {p.issues.join('; ')}</li>)}
        </ul>
      )}

      <div className="card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs uppercase"><tr>
            <th className="table-cell text-left">Beneficiary</th><th className="table-cell text-left">Type</th>
            <th className="table-cell text-left">Bank</th><th className="table-cell text-left">Paid from</th>
            <th className="table-cell text-right">Amount (₹)</th><th className="table-cell text-left">Mode</th>
            <th className="table-cell text-left">Narration</th><th className="table-cell" />
          </tr></thead>
          <tbody>
            {lines.length === 0 && <tr><td colSpan={8} className="table-cell text-center text-gray-400 py-6">Nothing added yet</td></tr>}
            {lines.map((l) => (
              <tr key={l.id} className={`border-t border-gray-100 align-top ${l.issues.length ? 'bg-red-50' : ''}`}>
                <td className="table-cell font-medium">{l.beneficiaryName}
                  {l.issues.length > 0 && <div className="text-xs text-red-600 font-normal">{l.issues.join('; ')}</div>}
                </td>
                <td className="table-cell text-xs text-gray-500">{l.type === 'SALARY' ? 'Salary' : l.type === 'REIMBURSEMENT' ? 'Claim' : 'Other'}</td>
                <td className="table-cell text-xs text-gray-600">{l.ifsc}<br />{l.account}</td>
                <td className="table-cell">
                  {draft ? (
                    <select className="input-field text-xs" value={l.entityId ?? ''} onChange={(e) => saveLine(l, { entityId: e.target.value ? Number(e.target.value) : null })}>
                      <option value="">{l.entityOverride || !l.entityName ? '— none —' : `${l.entityName} (default)`}</option>
                      {entities.map((en) => <option key={en.id} value={en.id}>{en.name}</option>)}
                    </select>
                  ) : l.entityName}
                </td>
                <td className="table-cell text-right">
                  {draft ? <input key={`${l.id}-${l.amount}`} type="number" step="0.01" min={0} className="input-field w-28 text-right" defaultValue={l.amount}
                    onBlur={(e) => { const v = Number(e.target.value); if (v !== l.amount) saveLine(l, { amount: v }); }} />
                    : `₹${formatRupeesUI(l.amount)}`}
                  {l.sourceAmount != null && Math.abs(l.sourceAmount - l.amount) > 0.005 && (
                    <div className="text-xs text-amber-600">{l.type === 'SALARY' ? 'payslip' : 'claim'}: ₹{formatRupeesUI(l.sourceAmount)}</div>
                  )}
                </td>
                <td className="table-cell text-xs">{l.mode ?? '—'}</td>
                <td className="table-cell">
                  {draft ? <input key={`${l.id}-${l.narration}`} className="input-field text-xs w-64" defaultValue={l.narration}
                    onBlur={(e) => { if (e.target.value !== l.narration) saveLine(l, { narration: e.target.value }); }} />
                    : l.narration}
                </td>
                <td className="table-cell">{draft && <button className="text-red-500 text-xs hover:underline" onClick={() => deletePaymentLine(l.id).then(load)}>Remove</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card p-4">
        <h4 className="font-semibold text-gray-800 mb-2">Bank files</h4>
        <div className="flex flex-wrap gap-3">
          {view.entities.map((e) => (
            <button key={String(e.entityId)} className="btn-primary" disabled={!e.entityId}
              onClick={() => e.entityId && download(e.entityId, e.entityName)}>
              ↓ {e.entityName} — {e.count} payments, ₹{formatRupeesUI(e.total)}{e.issueCount > 0 && ` (${e.issueCount} to fix)`}
            </button>
          ))}
          {draft && lines.length > 0 && <button className="btn-secondary" onClick={markPaid}>✓ Mark batch as paid</button>}
        </div>
        <p className="text-xs text-gray-400 mt-2">
          Mode: IFT for Kotak (KKBK) accounts, NEFT otherwise, RTGS above ₹2,00,000. Mark as paid only after the bank has accepted the file.
        </p>
      </div>
    </div>
  );
};

const SalaryPanel: React.FC<{ batchId: number; onDone: (m: string) => void }> = ({ batchId, onDone }) => {
  const [runs, setRuns] = useState<any[]>([]);
  const [runId, setRunId] = useState('');
  const [err, setErr] = useState('');
  useEffect(() => {
    listPayrollRuns().then((r) => {
      const fin = (r.data as any[]).filter((x) => x.status === 'FINALIZED');
      setRuns(fin); if (fin[0]) setRunId(String(fin[0].id));
    });
  }, []);
  const add = async () => {
    try {
      const r = await addPaymentSalaries(batchId, Number(runId));
      onDone(`Added ${r.data.added} salaries${r.data.alreadyPlanned ? `, ${r.data.alreadyPlanned} already in a batch` : ''}${r.data.zero ? `, ${r.data.zero} with nil net pay skipped` : ''}.`);
    } catch (e) { setErr(errMsg(e, 'Failed')); }
  };
  return (
    <div className="card p-4 flex flex-wrap items-end gap-3">
      <div>
        <label className="label">Finalized payroll run</label>
        <select className="input-field" value={runId} onChange={(e) => setRunId(e.target.value)}>
          {runs.map((r) => <option key={r.id} value={r.id}>{MONTHS[r.month - 1]} {r.year}</option>)}
        </select>
      </div>
      <button className="btn-primary" disabled={!runId} onClick={add}>Add salaries</button>
      {runs.length === 0 && <p className="text-sm text-gray-500">No finalized runs. Finalize the month in Payroll first.</p>}
      {err && <p className="text-red-600 text-sm">{err}</p>}
    </div>
  );
};

const ReimbPanel: React.FC<{ batchId: number; onDone: (m: string) => void }> = ({ batchId, onDone }) => {
  const [rows, setRows] = useState<any[]>([]);
  const [sel, setSel] = useState<Set<number>>(new Set());
  useEffect(() => { getReimbursementCandidates().then((r) => { setRows(r.data); setSel(new Set(r.data.map((x: any) => x.id))); }); }, []);
  const toggle = (id: number) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const add = async () => { const r = await addPaymentReimbursements(batchId, Array.from(sel)); onDone(`Added ${r.data.added} claims.`); };
  return (
    <div className="card p-4 space-y-2">
      {rows.length === 0 ? <p className="text-sm text-gray-500">No approved, unpaid claims waiting.</p> : (
        <>
          <div className="max-h-64 overflow-y-auto text-sm">
            {rows.map((c) => (
              <label key={c.id} className="flex items-center gap-3 py-1 border-b border-gray-50">
                <input type="checkbox" checked={sel.has(c.id)} onChange={() => toggle(c.id)} />
                <span className="w-24 font-mono text-xs">{c.claimNumber}</span>
                <span className="flex-1">{c.staffName}</span>
                <span>₹{formatRupeesUI(c.amount)}</span>
              </label>
            ))}
          </div>
          <button className="btn-primary" disabled={sel.size === 0} onClick={add}>Add {sel.size} claim(s)</button>
        </>
      )}
    </div>
  );
};

const OtherPanel: React.FC<{ batchId: number; entities: Entity[]; onDone: () => void }> = ({ batchId, entities, onDone }) => {
  const [who, setWho] = useState('');
  const [staff, setStaff] = useState<any[]>([]);
  const [payees, setPayees] = useState<any[]>([]);
  const [amount, setAmount] = useState('');
  const [narration, setNarration] = useState('');
  const [entityId, setEntityId] = useState('');
  const [err, setErr] = useState('');
  useEffect(() => {
    getStaff().then((r) => setStaff((r.data as any[]).filter((s) => s.isActive)));
    listPayees().then((r) => setPayees((r.data as any[]).filter((p) => p.isActive)));
  }, []);
  const add = async () => {
    setErr('');
    const [kind, idStr] = who.split(':');
    if (!idStr) { setErr('Choose who to pay'); return; }
    try {
      await addPaymentOther(batchId, {
        [kind === 's' ? 'staffId' : 'payeeId']: Number(idStr), amount: Number(amount), narration, entityId: entityId || undefined,
      });
      onDone();
    } catch (e) { setErr(errMsg(e, 'Failed')); }
  };
  return (
    <div className="card p-4 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
      <div>
        <label className="label">Pay to</label>
        <select className="input-field" value={who} onChange={(e) => setWho(e.target.value)}>
          <option value="">— Select —</option>
          <optgroup label="Payees">{payees.map((p) => <option key={`p${p.id}`} value={`p:${p.id}`}>{p.name}</option>)}</optgroup>
          <optgroup label="Staff">{staff.map((s) => <option key={`s${s.id}`} value={`s:${s.id}`}>{s.staffName}</option>)}</optgroup>
        </select>
      </div>
      <div><label className="label">Amount (₹)</label><input type="number" min={0} step="0.01" className="input-field" value={amount} onChange={(e) => setAmount(e.target.value)} /></div>
      <div><label className="label">Narration</label><input className="input-field" value={narration} placeholder="e.g. Rent October 2026" onChange={(e) => setNarration(e.target.value)} /></div>
      <div>
        <label className="label">Paid from (optional override)</label>
        <select className="input-field" value={entityId} onChange={(e) => setEntityId(e.target.value)}>
          <option value="">Default for payee</option>{entities.map((en) => <option key={en.id} value={en.id}>{en.name}</option>)}
        </select>
      </div>
      <div className="md:col-span-4 flex items-center gap-3"><button className="btn-primary" onClick={add}>Add payment</button>{err && <span className="text-red-600 text-sm">{err}</span>}</div>
    </div>
  );
};

// ── PAYEE MASTER ─────────────────────────────────────────────────────────────

const blankPayee = { name: '', bankAccountNumber: '', bankIfsc: '', bankName: '', paymentEntityId: '', notes: '' };

const PayeesTab: React.FC = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [entities, setEntities] = useState<Entity[]>([]);
  const [form, setForm] = useState<any>(blankPayee);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [error, setError] = useState('');
  const load = () => listPayees().then((r) => setRows(r.data));
  useEffect(() => { load(); getBillingEntities().then((r) => setEntities(r.data)); }, []);

  const save = async () => {
    setError('');
    try {
      const payload = { ...form, paymentEntityId: form.paymentEntityId || null };
      if (editingId) await updatePayee(editingId, payload); else await createPayee(payload);
      setForm(blankPayee); setEditingId(null); load();
    } catch (e) { setError(errMsg(e, 'Could not save')); }
  };
  const edit = (p: any) => {
    setEditingId(p.id);
    setForm({ name: p.name, bankAccountNumber: p.bankAccountNumber, bankIfsc: p.bankIfsc, bankName: p.bankName || '', paymentEntityId: p.paymentEntityId ? String(p.paymentEntityId) : '', notes: p.notes || '', isActive: p.isActive });
  };
  const set = (k: string) => (e: React.ChangeEvent<any>) => setForm({ ...form, [k]: k === 'bankIfsc' ? e.target.value.toUpperCase() : e.target.value });

  return (
    <div className="space-y-6">
      <div className="card p-4">
        <h3 className="font-semibold text-gray-800 mb-3">{editingId ? 'Edit payee' : 'Add payee'} <span className="text-xs font-normal text-gray-400">— landlords, vendors, anyone paid who is not on the staff list</span></h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div><label className="label">Name (as per bank)</label><input className="input-field" value={form.name} onChange={set('name')} /></div>
          <div><label className="label">Account number</label><input className="input-field" value={form.bankAccountNumber} onChange={set('bankAccountNumber')} /></div>
          <div><label className="label">IFSC</label><input className="input-field uppercase" maxLength={11} value={form.bankIfsc} onChange={set('bankIfsc')} /></div>
          <div><label className="label">Bank name (optional)</label><input className="input-field" value={form.bankName} onChange={set('bankName')} /></div>
          <div>
            <label className="label">Paid from</label>
            <select className="input-field" value={form.paymentEntityId} onChange={set('paymentEntityId')}>
              <option value="">— Select entity —</option>{entities.map((en) => <option key={en.id} value={en.id}>{en.name}</option>)}
            </select>
          </div>
          <div><label className="label">Notes</label><input className="input-field" value={form.notes} onChange={set('notes')} /></div>
        </div>
        {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
        <div className="mt-3 flex gap-2">
          <button className="btn-primary" onClick={save}>{editingId ? 'Update' : 'Add payee'}</button>
          {editingId && <button className="btn-secondary" onClick={() => { setEditingId(null); setForm(blankPayee); }}>Cancel</button>}
        </div>
      </div>
      <div className="card p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs uppercase"><tr>
            <th className="table-cell text-left">Name</th><th className="table-cell text-left">Account</th><th className="table-cell text-left">IFSC</th>
            <th className="table-cell text-left">Paid from</th><th className="table-cell" />
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={5} className="table-cell text-center text-gray-400 py-6">No payees yet</td></tr>}
            {rows.map((p) => (
              <tr key={p.id} className={`border-t border-gray-100 ${p.isActive ? '' : 'opacity-50'}`}>
                <td className="table-cell font-medium">{p.name}{!p.isActive && ' (inactive)'}</td>
                <td className="table-cell">{p.bankAccountNumber}</td><td className="table-cell">{p.bankIfsc}</td>
                <td className="table-cell">{p.paymentEntity?.name || '—'}</td>
                <td className="table-cell space-x-3">
                  <button className="text-blue-600 hover:underline" onClick={() => edit(p)}>Edit</button>
                  <button className="text-red-500 hover:underline" onClick={() => { if (window.confirm('Delete this payee?')) deletePayee(p.id).then(load); }}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Payments;
